import React, { useCallback, useEffect, useMemo, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil, Search } from "lucide-react";
import { toast } from "sonner";

/**
 * GenericList: reusable CRUD list backed by /api/records/{kind}
 * Props:
 *   kind (string), title, subtitle, newLabel, fields: [{key,label,type?}], testKey
 */
export default function GenericList({ kind, title, subtitle, newLabel, fields, testKey }) {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  const emptyForm = useMemo(() => Object.fromEntries(fields.map((f) => [f.key, ""])), [fields]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/records/${kind}`);
      setItems(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  }, [kind]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...emptyForm, ...(r.data || {}) }); setOpen(true); };

  const submit = async () => {
    setLoading(true);
    try {
      const data = { ...form };
      // strip empties for cleanliness
      Object.keys(data).forEach((k) => data[k] === "" && delete data[k]);
      if (editing) { await api.patch(`/records/${kind}/${editing.record_id}`, { data }); toast.success(t("common.save")); }
      else { await api.post(`/records/${kind}`, { data }); toast.success(t("common.create")); }
      setOpen(false); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(t("common.confirm") + "?")) return;
    try { await api.delete(`/records/${kind}/${id}`); toast.success(t("common.delete")); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const filtered = items.filter((r) => JSON.stringify(r.data || {}).toLowerCase().includes(q.toLowerCase()));

  return (
    <DashboardLayout
      title={title}
      subtitle={subtitle}
      right={
        <button data-testid={`${testKey}-new`} onClick={openNew} className="btn-scale h-9 px-3 rounded-md bg-blue-500 text-white font-medium text-sm flex items-center gap-2 hover:bg-blue-400">
          <Plus className="w-4 h-4" /> {newLabel}
        </button>
      }
    >
      <div className="flex items-center gap-2 mb-6">
        <div className="relative flex-1 max-w-sm">
          <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid={`${testKey}-search`} value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")} className="w-full pl-9 pr-3 h-9 bg-zinc-900 border border-zinc-800 rounded-md text-sm text-zinc-100" />
        </div>
      </div>

      <div className="rounded-xl border border-zinc-800 overflow-hidden" data-testid={`${testKey}-table`}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-zinc-500 border-b border-zinc-800 bg-zinc-900/40">
              {fields.slice(0, 4).map((f) => <th key={f.key} className="p-3 font-medium">{f.label}</th>)}
              <th className="p-3 font-medium text-right">{t("common.actions")}</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.record_id} className="border-b border-zinc-800 hover:bg-zinc-900/40">
                {fields.slice(0, 4).map((f) => (
                  <td key={f.key} className="p-3 text-zinc-300">
                    {String((r.data || {})[f.key] ?? "—")}
                  </td>
                ))}
                <td className="p-3 text-right">
                  <div className="inline-flex items-center gap-1">
                    <button data-testid={`${testKey}-edit-${r.record_id}`} onClick={() => openEdit(r)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-blue-300 hover:bg-zinc-800"><Pencil className="w-3.5 h-3.5" /></button>
                    <button data-testid={`${testKey}-del-${r.record_id}`} onClick={() => remove(r.record_id)} className="btn-scale h-8 w-8 grid place-items-center rounded-md text-zinc-400 hover:text-rose-400 hover:bg-zinc-800"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-zinc-500 text-sm">{t("common.no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-100 max-w-2xl" data-testid={`${testKey}-dialog`}>
          <DialogHeader><DialogTitle>{editing ? t("common.edit") : newLabel}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
            {fields.map((f) => (
              <label key={f.key} className={f.wide ? "md:col-span-2" : ""}>
                <span className="overline block mb-1.5">{f.label}</span>
                {f.type === "textarea" ? (
                  <textarea data-testid={`${testKey}-field-${f.key}`} rows={3} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm" />
                ) : f.type === "select" ? (
                  <select data-testid={`${testKey}-field-${f.key}`} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-2 text-sm">
                    <option value="">—</option>
                    {(f.options || []).map((o) => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
                  </select>
                ) : (
                  <input data-testid={`${testKey}-field-${f.key}`} type={f.type || "text"} value={form[f.key] || ""} onChange={(e) => setForm({ ...form, [f.key]: e.target.value })} className="w-full h-9 bg-zinc-950 border border-zinc-800 rounded-md px-3 text-sm" />
                )}
              </label>
            ))}
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="btn-scale h-9 px-3 rounded-md border border-zinc-700 text-zinc-300 text-sm">{t("common.cancel")}</button>
            <button data-testid={`${testKey}-save`} disabled={loading} onClick={submit} className="btn-scale h-9 px-4 rounded-md bg-blue-500 text-white text-sm font-medium hover:bg-blue-400 disabled:opacity-70">{t("common.save")}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
